import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Plus, 
  Trash2,
  Save,
  UserPlus,
  AlertCircle,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------
// Live Planning-preview strip — telt eligible spelers per taak
// en waarschuwt als de pool te klein is voor auto-generatie.
// ---------------------------------------------------------------
const PlanningPreviewStrip = ({ players }) => {
  // Alleen spelers met een naam ingevuld tellen mee (leeg rijen negeren)
  const named = players.filter((p) => (p.first_name || "").trim());
  const totalPlayers = named.filter((p) => !p.is_manager).length;
  const totalManagers = named.filter((p) => p.is_manager).length;
  const keepers = named.filter((p) => p.is_goalkeeper && !p.is_manager).length;
  const fluiters = named.filter((p) => p.is_referee && !p.is_manager).length;
  const rijders = named.filter((p) => p.can_drive !== false && !p.is_manager).length;

  const items = [
    {
      key: "totaal",
      label: "Spelers",
      count: totalPlayers,
      icon: <UserPlus className="w-4 h-4" />,
      hint: "Totaal aantal actieve spelers in het team",
      warn: totalPlayers < 11 ? `Slechts ${totalPlayers} spelers — je hebt er ~11 nodig per wedstrijd` : null,
    },
    {
      key: "keepers",
      label: "Keepers",
      count: keepers,
      color: "amber",
      hint: "Eligible om automatisch als keeper ingepland te worden",
      warn:
        keepers === 0
          ? "Geen keepers aangevinkt — geen keeper wordt automatisch ingepland"
          : null,
    },
    {
      key: "fluiters",
      label: "Fluiters",
      count: fluiters,
      color: "purple",
      hint: "Eligible om automatisch te fluiten (bij thuiswedstrijden — 2 per wedstrijd nodig)",
      warn:
        fluiters === 0
          ? "Geen fluiters aangevinkt — geen fluit-taak wordt automatisch ingepland"
          : fluiters === 1
          ? "Slechts 1 fluiter — 2 nodig per thuiswedstrijd"
          : null,
    },
    {
      key: "rijders",
      label: "Rijders",
      count: rijders,
      color: "orange",
      hint: "Eligible om automatisch te rijden (bij uitwedstrijden — 2 per wedstrijd nodig)",
      warn:
        rijders === 0
          ? "Geen rijders aangevinkt — geen speler wordt automatisch ingepland als rijder"
          : rijders === 1
          ? "Slechts 1 rijder — 2 nodig per uitwedstrijd"
          : null,
    },
    {
      key: "managers",
      label: "Managers",
      count: totalManagers,
      color: "slate",
      hint: "Managers coachen alle wedstrijden en rijden bij uitwedstrijden (2 per uitwedstrijd nodig)",
      warn:
        totalManagers < 2 ? `${totalManagers} manager(s) — 2 nodig als rijders bij uitwedstrijden` : null,
    },
  ];

  const colorMap = {
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    purple: "text-purple-700 bg-purple-50 border-purple-200",
    orange: "text-orange-700 bg-orange-50 border-orange-200",
    slate: "text-slate-700 bg-slate-50 border-slate-200",
  };

  return (
    <TooltipProvider>
      <div
        className="mb-6 bg-white border border-slate-200 rounded-xl shadow-sm p-4"
        data-testid="planning-preview-strip"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Planning voorbeeld
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Hoeveel spelers automatisch beschikbaar zijn per taak bij de volgende auto-generatie
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {items.map((it) => (
            <Tooltip key={it.key}>
              <TooltipTrigger asChild>
                <div
                  className={`relative rounded-lg border px-3 py-2.5 cursor-help transition-colors ${
                    it.warn
                      ? "bg-rose-50 border-rose-200 text-rose-800"
                      : it.color
                      ? colorMap[it.color]
                      : "bg-slate-50 border-slate-200 text-slate-700"
                  }`}
                  data-testid={`preview-${it.key}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium uppercase tracking-wider opacity-70">
                      {it.label}
                    </span>
                    {it.warn ? (
                      <AlertCircle className="w-3.5 h-3.5" />
                    ) : it.key !== "totaal" && it.count > 0 ? (
                      <Check className="w-3.5 h-3.5 opacity-60" />
                    ) : null}
                  </div>
                  <div className="text-2xl font-bold leading-none">{it.count}</div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[260px]">
                <p>{it.hint}</p>
                {it.warn && <p className="mt-1 font-semibold text-rose-500">{it.warn}</p>}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
};

export const Players = () => {
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const inputRefs = useRef({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get the single season
      const seasonsRes = await axios.get(`${API}/seasons`);
      if (seasonsRes.data.length === 0) {
        toast.error("Geen seizoen gevonden");
        navigate("/");
        return;
      }
      
      const currentSeason = seasonsRes.data[0];
      setSeason(currentSeason);
      
      const playersRes = await axios.get(`${API}/players?season_id=${currentSeason.id}`);
      
      // Ensure we have at least 15 rows
      const existingPlayers = playersRes.data.map(p => ({
        ...p,
        // Legacy players (before this field existed) default to eligible.
        can_drive: p.can_drive === undefined ? true : p.can_drive,
        isNew: false,
      }));
      const emptyRows = Math.max(0, 15 - existingPlayers.length);
      const emptyPlayers = Array.from({ length: emptyRows }, (_, i) => ({
        id: `new-${i}`,
        first_name: "",
        last_name: "",
        is_manager: false,
        is_goalkeeper: false,
        is_referee: false,
        can_drive: true,
        season_id: currentSeason.id,
        isNew: true
      }));
      
      setPlayers([...existingPlayers, ...emptyPlayers]);
    } catch (error) {
      toast.error("Gegevens laden mislukt");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...players];
    updated[index][field] = value;
    setPlayers(updated);
    setHasChanges(true);
  };

  const handleAddRow = () => {
    setPlayers([
      ...players,
      {
        id: `new-${Date.now()}`,
        first_name: "",
        last_name: "",
        is_manager: false,
        is_goalkeeper: false,
        is_referee: false,
        can_drive: true,
        season_id: season.id,
        isNew: true
      }
    ]);
  };

  const handleDelete = async (index) => {
    const player = players[index];
    
    if (!player.isNew && player.id && !player.id.startsWith("new-")) {
      try {
        await axios.delete(`${API}/players/${player.id}`);
        toast.success("Speler verwijderd");
      } catch (error) {
        toast.error("Speler verwijderen mislukt");
        return;
      }
    }
    
    const updated = players.filter((_, i) => i !== index);
    setPlayers(updated);
  };

  const handleSave = async () => {
    const validPlayers = players.filter(p => p.first_name.trim());

    setSaving(true);
    try {
      // Separate existing and new players
      const existingPlayers = validPlayers.filter(p => !p.isNew && !p.id.startsWith("new-"));
      const newPlayers = validPlayers.filter(p => p.isNew || p.id.startsWith("new-"));
      
      // Update existing players
      for (const player of existingPlayers) {
        await axios.put(`${API}/players/${player.id}`, {
          first_name: player.first_name,
          last_name: player.last_name,
          is_manager: player.is_manager || false,
          is_goalkeeper: player.is_goalkeeper || false,
          is_referee: player.is_referee || false,
          can_drive: player.can_drive !== false,
          season_id: season.id
        });
      }
      
      // Create new players
      if (newPlayers.length > 0) {
        await axios.post(`${API}/players/bulk`, newPlayers.map(p => ({
          first_name: p.first_name,
          last_name: p.last_name,
          is_manager: p.is_manager || false,
          is_goalkeeper: p.is_goalkeeper || false,
          is_referee: p.is_referee || false,
          can_drive: p.can_drive !== false,
          season_id: p.season_id
        })));
      }

      toast.success("Spelers opgeslagen");
      setHasChanges(false);
      fetchData();
    } catch (error) {
      toast.error("Spelers opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, index, field) => {
    if (e.key === "Tab" && !e.shiftKey && field === "last_name" && index === players.length - 1) {
      e.preventDefault();
      handleAddRow();
      setTimeout(() => {
        const newIndex = players.length;
        if (inputRefs.current[`${newIndex}-first_name`]) {
          inputRefs.current[`${newIndex}-first_name`].focus();
        }
      }, 50);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="players-page" className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="h-10 w-10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-1">
              {season?.club_name} / {season?.team_name}
            </p>
            <h1 
              className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Spelers
            </h1>
            <p className="text-slate-500 mt-1">Beheer je teamlijst</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleAddRow}
            data-testid="add-player-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Speler toevoegen
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-slate-900 hover:bg-slate-800 min-w-[120px]"
            data-testid="save-players-btn"
          >
            {saving ? (
              <div className="spinner w-4 h-4 border-white border-t-transparent" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                Opslaan
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Live Planning-preview strip — reageert real-time op checkbox changes */}
      <PlanningPreviewStrip players={players} />

      {/* Players Table */}
      <TooltipProvider>
        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <CardHeader className="bg-slate-50 border-b border-slate-200 py-3 px-0">
            <div className="grid grid-cols-12 gap-2 px-4">
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider">#</div>
              <div className="col-span-3 text-xs uppercase font-medium text-slate-500 tracking-wider">Voornaam</div>
              <div className="col-span-3 text-xs uppercase font-medium text-slate-500 tracking-wider">Achternaam</div>
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider text-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help">Keeper</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Alleen aangevinkte spelers worden automatisch ingepland als keeper. Als meerdere zijn aangevinkt, rouleert de planning. Handmatig instellen blijft altijd mogelijk.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider text-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help">Fluit</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Alleen aangevinkte spelers worden automatisch ingepland voor de Fluit-taak (bij thuiswedstrijden). Handmatig instellen blijft altijd mogelijk.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider text-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help">Rijdt</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Alleen aangevinkte spelers worden automatisch ingepland als rijder bij uitwedstrijden. Handmatig instellen blijft altijd mogelijk.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="col-span-2"></div>
            </div>
          </CardHeader>
          <div className="divide-y divide-slate-100">
            {players.map((player, index) => (
              <div 
                key={player.id} 
                className="grid grid-cols-12 gap-2 px-4 py-1 hover:bg-slate-50 transition-colors group items-center"
                data-testid={`player-row-${index}`}
              >
                <div className="col-span-1 text-sm text-slate-400 font-medium">
                  {index + 1}
                </div>
                <div className="col-span-3">
                  <Input
                    ref={el => inputRefs.current[`${index}-first_name`] = el}
                    value={player.first_name}
                    onChange={(e) => handleChange(index, "first_name", e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index, "first_name")}
                    placeholder="Voornaam"
                    className="h-9 border-transparent bg-transparent hover:border-slate-200 focus:border-slate-300 focus:bg-white"
                    data-testid={`player-first-name-${index}`}
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    ref={el => inputRefs.current[`${index}-last_name`] = el}
                    value={player.last_name}
                    onChange={(e) => handleChange(index, "last_name", e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index, "last_name")}
                    placeholder="Achternaam"
                    className="h-9 border-transparent bg-transparent hover:border-slate-200 focus:border-slate-300 focus:bg-white"
                    data-testid={`player-last-name-${index}`}
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  <Checkbox
                    checked={player.is_goalkeeper || false}
                    onCheckedChange={(checked) => handleChange(index, "is_goalkeeper", checked)}
                    data-testid={`player-goalkeeper-${index}`}
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  <Checkbox
                    checked={player.is_referee || false}
                    onCheckedChange={(checked) => handleChange(index, "is_referee", checked)}
                    data-testid={`player-referee-${index}`}
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  <Checkbox
                    checked={player.can_drive !== false}
                    onCheckedChange={(checked) => handleChange(index, "can_drive", checked)}
                    data-testid={`player-can-drive-${index}`}
                  />
                </div>
                <div className="col-span-2 flex justify-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(index)}
                    className="h-8 w-8 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-player-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </TooltipProvider>

      {/* Help Text */}
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <UserPlus className="w-4 h-4" />
        <span>Druk op Tab op de laatste rij om een nieuwe speler toe te voegen</span>
      </div>
    </div>
  );
};
